import { memo, useCallback } from 'react';
import {
  Label,
  Textarea,
  Separator,
  SelectOrCreate,
  SelectItem,
} from '@flote/ui';
import { Package, User, MapPin } from 'lucide-react';
import { useGooglePlaces, type ParsedAddress } from '@flote/app-core';
import { RecipientInput, AddressInput, TimeWindowInput, CompletionRequirements } from '../inputs';
import { TeamDriverAssignment } from './TeamDriverAssignment';
import type {
  PickupState,
  AddressState,
  RecipientState,
  TimeWindowState,
  CompletionRequirementsState,
} from '../hooks';
import type { Team, Recipient, Destination, Driver } from '@flote/contracts/http';

interface PickupSectionProps {
  pickup: PickupState;
  // Team/Driver
  teams: Team[];
  drivers: Driver[];
  isLoadingDrivers: boolean;
  onTeamChange: (teamId: string) => void;
  onDriverChange: (driverId: string) => void;
  onAutoAssignChange: (autoAssign: boolean) => void;
  // Recipient
  recipients: Recipient[];
  selectedRecipient: Recipient | undefined;
  onRecipientIdChange: (id: string | undefined) => void;
  onRecipientModeChange: (mode: 'select' | 'create') => void;
  onRecipientChange: (field: keyof RecipientState, value: string) => void;
  // Destination
  destinations: Destination[];
  selectedDestination: Destination | undefined;
  onDestinationIdChange: (id: string | undefined) => void;
  onDestinationModeChange: (mode: 'select' | 'create') => void;
  onAddressChange: (field: keyof AddressState, value: string) => void;
  onAddressSelect: (address: AddressState) => void;
  // Notes, Time, Requirements
  onNotesChange: (notes: string) => void;
  onTimeWindowChange: (field: keyof TimeWindowState, value: string) => void;
  onCompletionRequirementsChange: (field: keyof CompletionRequirementsState, value: boolean) => void;
  // Misc
  errors: Record<string, string>;
  disabled?: boolean;
  isLinkedTask?: boolean;
  noTeamValue: string;
  noDriverValue: string;
  showDriverSelect?: boolean;
}

export const PickupSection = memo<PickupSectionProps>(({
  pickup,
  teams,
  drivers,
  isLoadingDrivers,
  onTeamChange,
  onDriverChange,
  onAutoAssignChange,
  recipients,
  selectedRecipient,
  onRecipientIdChange,
  onRecipientModeChange,
  onRecipientChange,
  destinations,
  selectedDestination,
  onDestinationIdChange,
  onDestinationModeChange,
  onAddressChange,
  onAddressSelect,
  onNotesChange,
  onTimeWindowChange,
  onCompletionRequirementsChange,
  errors,
  disabled = false,
  isLinkedTask = false,
  noTeamValue,
  noDriverValue,
  showDriverSelect = true,
}) => {
  // Handle Google Places autocomplete selection
  const handlePlaceSelect = useCallback((parsedAddress: ParsedAddress) => {
    onAddressSelect({
      search: parsedAddress.formattedAddress,
      streetNumber: parsedAddress.number,
      streetAddress: parsedAddress.street,
      apartment: parsedAddress.apartment,
      city: parsedAddress.city,
      state: parsedAddress.state,
      postalCode: parsedAddress.postalCode,
    });
  }, [onAddressSelect]);

  const { inputRef: addressSearchRef } = useGooglePlaces(
    import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    { onPlaceSelect: handlePlaceSelect }
  );

  const isNewRecipient = pickup.recipientMode === 'create';
  const isNewDestination = pickup.destinationMode === 'create';

  return (
    <>
      {/* Pickup header for linked mode */}
      {isLinkedTask && (
        <div className="flex items-center gap-2 text-sm font-medium">
          <Package className="h-4 w-4 text-muted-foreground" />
          Pickup Details
        </div>
      )}

      {/* Team Assignment - only show for standalone pickups */}
      {!isLinkedTask && (
        <>
          <TeamDriverAssignment
            teams={teams}
            selectedTeamId={pickup.selectedTeamId}
            onTeamChange={onTeamChange}
            autoAssign={pickup.autoAssign}
            onAutoAssignChange={onAutoAssignChange}
            drivers={drivers}
            selectedDriverId={pickup.selectedDriverId}
            onDriverChange={onDriverChange}
            isLoadingDrivers={isLoadingDrivers}
            disabled={disabled}
            noTeamValue={noTeamValue}
            noDriverValue={noDriverValue}
            showDriverSelect={showDriverSelect}
          />

          <Separator />
        </>
      )}

      {/* Recipient Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <User className="h-4 w-4 text-muted-foreground" />
          Pickup Contact
        </div>

        <SelectOrCreate.Root
          selectedValue={pickup.selectedRecipientId}
          onSelectedValueChange={onRecipientIdChange}
          onModeChange={onRecipientModeChange}
          defaultMode={recipients.length > 0 ? 'select' : 'create'}
          disabled={disabled}
        >
          <div className="space-y-2">
            <Label>Select Existing Contact</Label>
            <SelectOrCreate.Select placeholder="Select a contact">
              {recipients.map((recipient) => (
                <SelectItem key={recipient.id} value={recipient.id}>
                  {recipient.name}
                  {recipient.phone && (
                    <span className="ml-1 text-muted-foreground">({recipient.phone})</span>
                  )}
                </SelectItem>
              ))}
            </SelectOrCreate.Select>
          </div>

          <SelectOrCreate.Preview>
            {selectedRecipient && (
              <>
                <p className="text-sm font-medium">{selectedRecipient.name}</p>
                {selectedRecipient.phone && (
                  <p className="text-xs text-muted-foreground">{selectedRecipient.phone}</p>
                )}
              </>
            )}
          </SelectOrCreate.Preview>

          <SelectOrCreate.Button>
            {isNewRecipient ? 'Cancel' : 'Create New Contact'}
          </SelectOrCreate.Button>

          <SelectOrCreate.Form>
            <RecipientInput
              recipient={pickup.recipient}
              onRecipientChange={onRecipientChange}
              errors={errors}
              errorPrefix="pickup.recipient."
              disabled={disabled}
              nameLabel="Contact Name"
              namePlaceholder="Supplier contact name"
              phoneLabel="Contact Phone"
            />
          </SelectOrCreate.Form>
        </SelectOrCreate.Root>
      </div>

      <Separator />

      {/* Destination Section */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 text-sm font-medium">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          Pickup Location
        </div>

        <SelectOrCreate.Root
          selectedValue={pickup.selectedDestinationId}
          onSelectedValueChange={onDestinationIdChange}
          onModeChange={onDestinationModeChange}
          defaultMode={destinations.length > 0 ? 'select' : 'create'}
          disabled={disabled}
        >
          <div className="space-y-2">
            <Label>Select Existing Location</Label>
            <SelectOrCreate.Select placeholder="Select a location">
              {destinations.map((destination) => (
                <SelectItem key={destination.id} value={destination.id}>
                  {destination.address?.street && destination.address?.number
                    ? `${destination.address.number} ${destination.address.street}`
                    : 'Unknown address'}
                  {destination.address?.city && (
                    <span className="ml-1 text-muted-foreground">
                      ({destination.address.city})
                    </span>
                  )}
                </SelectItem>
              ))}
            </SelectOrCreate.Select>
          </div>

          <SelectOrCreate.Preview>
            {selectedDestination && (
              <>
                <p className="text-sm font-medium">
                  {selectedDestination.address?.number} {selectedDestination.address?.street}
                  {selectedDestination.address?.apartment && `, ${selectedDestination.address.apartment}`}
                </p>
                <p className="text-xs text-muted-foreground">
                  {selectedDestination.address?.city}, {selectedDestination.address?.state} {selectedDestination.address?.postalCode}
                </p>
                {selectedDestination.notes && (
                  <p className="text-xs text-muted-foreground mt-1 italic">{selectedDestination.notes}</p>
                )}
              </>
            )}
          </SelectOrCreate.Preview>

          <SelectOrCreate.Button>
            {isNewDestination ? 'Cancel' : 'Enter New Address'}
          </SelectOrCreate.Button>

          <SelectOrCreate.Form>
            <AddressInput
              address={pickup.address}
              onAddressChange={onAddressChange}
              inputRef={addressSearchRef}
              errors={errors}
              errorPrefix="pickup."
              disabled={disabled}
              searchLabel="Search Pickup Address"
              searchPlaceholder="Start typing to search for pickup address..."
            />
          </SelectOrCreate.Form>
        </SelectOrCreate.Root>
      </div>

      <Separator />

      {/* Notes Section */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="pickupNotes">Pickup Notes</Label>
          <Textarea
            id="pickupNotes"
            placeholder="Pickup instructions, gate codes, etc."
            value={pickup.notes}
            onChange={(e) => onNotesChange(e.target.value)}
            disabled={disabled}
            rows={2}
          />
        </div>
      </div>

      <Separator />

      {/* Time Window */}
      <TimeWindowInput
        timeWindow={pickup.timeWindow}
        onTimeWindowChange={onTimeWindowChange}
        disabled={disabled}
        idPrefix="pickup"
        afterLabel="Pickup After"
        beforeLabel="Pickup Before"
        afterDescription=""
        beforeDescription=""
        title="Pickup Time Window"
      />

      <Separator />

      {/* Completion Requirements */}
      <CompletionRequirements
        requirements={pickup.completionRequirements}
        onRequirementChange={onCompletionRequirementsChange}
        disabled={disabled}
        idPrefix="pickup"
        title="Pickup Requirements"
        description=""
        photoLabel="Photo of pickup"
        layout="row"
      />
    </>
  );
});

PickupSection.displayName = 'PickupSection';
